// COMPLAINT_SUBTYPES — nested by complaint_group SLUG at the outer level,
// keyed by complaint_type NAME at the inner level. Callsite:
//   COMPLAINT_SUBTYPES[form.complaint_group]?.[form.complaint_type] || []
// Both keys come from the form state; complaint_group is the slug set by
// the container from complaintData.typeToSlug[type]. Every list ends with
// "Other". Restructured 2026-07-23 to fix a bug where every type inside a
// group showed the same sub-type list (the outer flat map was group-keyed).
// business_inquiry deliberately absent — bots don't read it, UI doesn't
// offer it; AUTO_DEPT_MAP still routes it to Marketing.
export const COMPLAINT_SUBTYPES = {
  delay_complaint: {
    'Delay Order': [
      'Late delivery — less than 48 hours',
      'Late delivery — over 48 hours',
      'Other',
    ],
    'Wrong Address': [
      'Delivered to old address',
      'Wrong area',
      'Wrong building or tower',
      'Wrong floor or apartment',
      'Address change not applied same-day',
      'Other',
    ],
  },
  missing_items: {
    'Non-delivery': [
      'System shows delivered — customer denies receipt',
      'Delivery attempted — customer not reached',
      'Other',
    ],
    'Partial Delivery': [
      'Missing 5G gallons (short by N)',
      'Missing PET packs',
      'Missing free item/s',
      'Missing coupon booklet items',
      'Other',
    ],
  },
  delivery_inquiry: {
    'Delivery Timing Inquiry': [
      'Where is my order — ETA request',
      'Other',
    ],
  },
  quality_complaint: {
    'Taste': [
      'Off taste — unusual flavour',
      'Metallic taste',
      'Chlorine or chemical taste',
      'Detergents taste',
      'Plastic taste',
      'Salty or bitter taste',
      'Other',
    ],
    'Smell': [
      'Off smell — unusual odour',
      'Chemical smell',
      'Detergents smell',
      'Plastic smell',
      'Other',
    ],
    'Colour / Appearance': [
      'Cloudy or milky appearance',
      'Discoloured (yellow / brown / green tint)',
      'Sediment or particles visible',
      'Bubbles or fizz in still water',
      'Other',
    ],
    'Foreign Object': [
      'Insect inside container',
      'Solid particle / debris',
      'Plastic fragment',
      'Hair or fibre',
      'Unknown object',
      'Other',
    ],
    'Packaging Damage': [
      'Leaking gallon or bottle',
      'Cracked or broken container',
      'Loose cap / seal broken on arrival',
      'Damaged shrink wrap',
      'Missing or damaged label',
      'Other',
    ],
    'Expired or Wrong Product': [
      'Expired product delivered',
      'Wrong product delivered (different SKU)',
      'Old batch (near expiry) delivered',
      'Other',
    ],
  },
  cancel_order: {
    'Cancel-Delay': [
      'Customer waited too long',
      'Event or occasion passed',
      'Agent could not confirm ETA',
      'Other',
    ],
    'Cancel-Other': [
      'Duplicate order placed',
      'Price disagreement',
      'Emergency — no longer needed',
      'Wrong item ordered by customer',
      'Wrong quantity ordered',
      'Payment method issue',
      'Customer changed mind',
      'Found alternative supplier',
      'Other',
    ],
  },
  order_modification: {
    'Modify Address': [
      'Same-day change (before route closes)',
      'Next-day change',
      'New Ship-to needed',
      'Wrong area selected at order',
      'Other',
    ],
    'Modify Size/Qty': [
      'Increase quantity — same size',
      'Decrease quantity — same size',
      'Add SKU to order',
      'Change SKU in the order',
      'Remove SKU from order',
      'Other',
    ],
    'Modify Phone/Time': [
      'Change delivery time window',
      'Change contact phone number',
      'Change delivery day',
      'Other',
    ],
    'Exchange': [
      'Exchange for damaged goods',
      'Other',
    ],
  },
  refund_request: {
    'Refund Status': [
      'Refund not received',
      'Refund partially received',
      'Other',
    ],
    'Payment Discrepancy': [
      'Double charge',
      'Payment made — no order created',
      'Payment made — not showing on order',
      'Overcharged',
      'Undercharged',
      'Other',
    ],
  },
  payment_link: {
    'Payment Link': [
      'Link expired',
      'Link not received',
      'Link not working',
      'Change payment method',
      'Other',
    ],
  },
  driver_complaint: {
    'Driver Misconduct': [
      'Verbal abuse / shouting',
      'Overcharging',
      'Refused delivery',
      'Entered property without permission',
      'Inappropriate behaviour',
      'Damaged customer property',
      'Other',
    ],
    'Driver Behavior': [
      'Rude tone',
      'Unprofessional appearance',
      'Late arrival',
      'Refused to carry to floor/inside',
      'Left order at wrong door',
      'No acknowledgement / no doorbell',
      'Other',
    ],
    'Wrong Driver': [
      'Non-Abraaj vehicle',
      'Driver without ID / uniform',
      'Different driver than expected',
      'Unauthorised person on delivery',
      'Other',
    ],
  },
  general_inquiry: {
    'Order Status': [
      'Where is my order — ETA request',
      'Payment status confirmation',
      'Order not showing in app',
      'Other',
    ],
    'Product Info': [
      'Product availability',
      'Size or SKU details',
      'Free item eligibility',
      'New product info',
      'Ingredient or water source info',
      'Other',
    ],
    'Delivery Area': [
      'Is my area covered?',
      'Delivery schedule for area',
      'Delivery not available in my area',
      'Other',
    ],
  },
  price_inquiry: {
    'Price Inquiry': [
      'Product prices',
      'Bulk / B2B pricing',
      'Mosque pricing',
      'Coupon booklet pricing',
      'Price change since last order',
      'Other',
    ],
  },
};

export const RESOLUTION_TAKEN_OPTIONS = [
  'Re-delivery scheduled',
  'Refund processed',
  'Partial refund',
  'Replacement dispatched',
  'Driver escalated to supervisor',
  'Apology & follow-up call',
  'Maintenance visit booked',
  'Order corrected in system',
  'Customer educated / no action',
  'Escalated to manager',
  'No action required — resolved by customer',
  'Invoice shared',
];

// Responsibility attribution — required at ticket resolution. `value` is the
// canonical enum stored in DB; `label` is the display text on the segmented
// control. Legacy tickets (pre-migration) render as "not set".
export const RESPONSIBILITY_ATTRIBUTION_OPTIONS = [
  { value: 'company',  label: 'Company'  },
  { value: 'customer', label: 'Customer' },
  { value: 'external', label: 'External' },
];
export const RESPONSIBILITY_ATTRIBUTION_LABEL = Object.fromEntries(
  RESPONSIBILITY_ATTRIBUTION_OPTIONS.map((o) => [o.value, o.label])
);

export const DEPARTMENT_OPTIONS = [
  'Logistics',
  'Finance',
  'Quality Control',
  'Customer Service',
  'Sales',
  'Maintenance / Technical',
  'Management Escalation',
  'IT',
  'Marketing',
];

export const TICKET_STATUSES = [
  'open',
  'under_process',
  'pending_finance',
  'resolved',
  'closed',
];

// Single source of truth for the status filter dropdown (D4: 'In Progress'
// is a stale label — under_process is the only slug for that state).
export const TICKET_STATUS_LABELS = {
  new: 'New',
  escalated: 'Escalated',
  under_process: 'Under Process',
  pending_finance: 'Pending Finance',
  pending_customer_contact: 'Pending Customer Contact',
  resolved: 'Resolved',
  reopened: 'Reopened',
  closed: 'Closed',
};

export const FINANCE_GATE_GROUPS = [
  'refund_request',
  'cancel_order',
];

export const ORDER_SOURCE_OPTIONS = [
  'Phone Call',
  'WhatsApp',
  'Email',
  'Abraaj App',
  'Talabat',
  'Zyda',
  'Sheeel',
  'Snoonu',
  'Mashareeb',
  'Bylbait',
  'One Global',
  'Cari',
  'Salesman / Direct',
  'Walk-in',
  'Other',
];

export const CUSTOMER_SEGMENT_OPTIONS = [
  'Retail B2C',
  'Corporate B2B',
  'Government',
  'Charity',
  'Restaurant / F&B',
  'Hotel / Hospitality',
  'Mosque',
];

// Note: '1L' was removed 2026-09-13 — dead vocabulary (no SKU maps to it,
// Abraaj never had a 1L product). 6 legacy tickets carry '1L' in their stored
// product_type value; they still DISPLAY the value as a chip on TicketDetail
// (EditField multiselect renders any stored value verbatim), but the '1L'
// checkbox is gone from the picker.
export const PRODUCT_TYPE_OPTIONS = [
  '5 Gallon (19L)',
  '330ml',
  '200ml',
  '500ml',
  '1.5L',
  'Mixed / Multiple',
  'Tissues',
  'Ice Cubes',
  'Other',
];

export const KUWAIT_AREAS = [
  'Abdally',
  'Abdulla Al-Salem',
  'Abdullah Mubarak Al-Sabah',
  'Abu Ftaira',
  'Abu Halifa',
  'Adailiya',
  'Ahmadi Governorate Desert',
  'Airport',
  "Al Bida'a",
  'Al Masayel',
  'Al Mutlaa',
  'Al Mutlaa Residential 1',
  'Al Mutlaa Residential 10',
  'Al Mutlaa Residential 11',
  'Al Mutlaa Residential 12',
  'Al Mutlaa Residential 2',
  'Al Mutlaa Residential 3',
  'Al Mutlaa Residential 4',
  'Al Mutlaa Residential 5',
  'Al Mutlaa Residential 6',
  'Al Mutlaa Residential 7',
  'Al Mutlaa Residential 8',
  'Al Mutlaa Residential 9',
  'Al shadadyia Industrial',
  'Al Sheqaya',
  'Al Sour Gardens',
  'Al-Adan',
  'Al-Fintas',
  'Al-Fnaitees',
  'Al-Nuwaiseeb',
  'Al-Qurain',
  'Al-Qusour',
  'Al-Siddiq',
  'Al-Sikrab',
  'Ali Subah Al-Salem',
  'Amghara Industrial',
  'Andalus',
  'Ardhiya',
  'Ardhiya Herafiya',
  'Ardhiya Stores',
  'Ardiya Government',
  'Ashbeliah',
  'Auha Island',
  'Bar Al-Jahra Governorate',
  'Bayan',
  'Bhaith',
  'Bnaid Al-Qar',
  'Bubyan Island',
  'Daiya',
  'Dajeej',
  'Dasma',
  'Dasman',
  'Dhaher',
  'Doha Residential',
  'Dulay Az Zunif',
  'East Ahmadi',
  'East Sabah Al-Ahmad',
  'Egaila',
  'Fahad Al-Ahmad',
  'Fahaheel',
  'Faiha',
  'Failaka Island',
  'Farwaniya',
  'Ferdous',
  'Ghornata',
  'Hadiya',
  'Hawalli',
  'Hitteen',
  'Jaber Al-Ahmad',
  'Jaber Al-Ali',
  'Jabriya',
  'Jahra',
  'Jahra Camps',
  'Jahra Industrial Herafiya 1',
  'Janobyia Aljawakheer',
  'Jawakher Al Jahra',
  'Jleeb Al-Shiyoukh',
  'Kabd',
  'kabd Agricultural',
  'Kaerawan',
  'Kazima',
  'Khaitan',
  'Khaldiya',
  'Khiran City',
  'Kifan',
  'Kubbar Island',
  'Low costs',
  'Magwa',
  'Mahboula',
  'Mangaf',
  'Mansouriya',
  'Middle of Ahmadi',
  'Mina Abdulla',
  'Mina Abdullah Refinery',
  'Mina Al-Ahmadi Refinery',
  'Mina Doha',
  'Ministries Area',
  'Mirqab',
  'Mischan Island',
  'Mishrif',
  'Mubarak Al-Abdullah',
  'Mubarak Al-Kabeer',
  'Mubarakiya Camps',
  'Mubarakyia',
  'Naayem Industrial',
  'Naeem',
  'Nahda',
  'Nasseem',
  'North Ahmadi',
  'North West Jahra',
  'Northern Artificial island',
  'Northwest Sulaibikhat',
  'Nuzha',
  'Omariya',
  'Oyoun',
  'Qadsiya',
  'Qaruh Island',
  'Qasr',
  'Qibla',
  'Qortuba',
  'Rabiya',
  'Rai',
  'Rajim Khashman',
  'Rawda',
  'Rawdatain',
  'Rehab',
  'Riggai',
  'Riqqa',
  'Rumaithiya',
  'Saad Al-Abdulla',
  'Sabah Al Salem University',
  'Sabah Al-Ahmad 1',
  'Sabah Al-Ahmad 2',
  'Sabah Al-Ahmad 3',
  'Sabah Al-Ahmad 4',
  'Sabah Al-Ahmad 5',
  'Sabah Al-Ahmad 6',
  'Sabah Al-Ahmad Al-marine',
  'Sabah Al-Ahmad Investment',
  'Sabah Al-Ahmad Services',
  'Sabah Al-Nasser',
  'Sabah Al-Salem',
  'Sabahiya',
  'Salam',
  'Salmiya',
  'Salmy',
  'Salwa',
  'Sayhad Al Awazim',
  'Shaab',
  'Shadadiya',
  'Shalehat Al-Khiran',
  'Shalehat Al-Nuwaiseeb',
  'Shalehat Bneder',
  "Shalehat Dba'ayeh",
  'Shalehat Doha',
  "Shalehat Jlea'a",
  'Shalehat Kazima',
  'Shalehat Mina Abdullah',
  'Shalehat Subiya',
  'Shalehat Zoor',
  'Shamiya',
  'Sharq',
  'Shuaiba Industrial esterly',
  'Shuaiba Industrial Western',
  'Shuhada',
  'Shuwaikh',
  'Shuwaikh Educational',
  'Shuwaikh Industrial-1',
  'Shuwaikh Industrial-2',
  'Shuwaikh Industrial-3',
  'Shuwaikh Port',
  'Shuwaikh Sanitary',
  'South Abdullah Al-Mubarak',
  'South Ahmadi',
  'South Amghara',
  'South Khitan Shows',
  'South Saad Al-Abdulla 1',
  'South Saad Al-Abdulla 2',
  'South Saad Al-Abdulla 3',
  'South Saad Al-Abdulla 4',
  'South Saad Al-Abdulla 5',
  'South Saad Al-Abdulla 6',
  'South Subah AlAhamd',
  'South Subah AlAhamd 1',
  'South Subah AlAhamd 10',
  'South Subah AlAhamd 11',
  'South Subah AlAhamd 2',
  'South Subah AlAhamd 3',
  'South Subah AlAhamd 4',
  'South Subah AlAhamd 5',
  'South Subah AlAhamd 6',
  'South Subah AlAhamd 7',
  'South Subah AlAhamd 8',
  'South Subah AlAhamd 9',
  'South Subah AlAhamd Services',
  'South-Sabahiya',
  'Southern Artificial Island',
  'Subhan Industrial',
  'Subiya',
  'Sulaibikhat',
  'Sulaibikhat Cemetery',
  'Sulaibiya Agricultural',
  'Sulaibiya Industrial 1',
  'Sulaibiya Industrial 2',
  'Sulaibiya Residential',
  'Sulaibyia Industrial 3',
  'Surra',
  'Taima',
  'The Sea Front',
  'The Sea Front Hawalli',
  'Umm Al-Aish',
  'Umm Al-Maradim Island',
  'Umm Al-Namel Island',
  'Umm Hegoul',
  'Wafra',
  'Wafra Farms',
  'Wafra Residential',
  'Waha',
  'Warba Island',
  'West Abdullah Al-Mubarak',
  'West Abu Ftirah Hirafyia',
  'West Ahmadi',
  'Wista',
  'Yarmouk',
  'Zahra',
  'Zoor',
];

export const AREA_AR_TO_EN = {
  'أبو حليفة': 'Abu Halifa',
  'بر محافظة الأحمدي': 'Ahmadi Governorate Desert',
  'الشدادية الصناعية': 'Al shadadyia Industrial',
  'الفنطاس': 'Al-Fintas',
  'النويصيب': 'Al-Nuwaiseeb',
  'علي صباح السالم': 'Ali Subah Al-Salem',
  'الظهر': 'Dhaher',
  'ضليع الزنيف': 'Dulay Az Zunif',
  'شرق الأحمدي': 'East Ahmadi',
  'شرق صباح الاحمد': 'East Sabah Al-Ahmad',
  'العقيلة': 'Egaila',
  'فهد الأحمد': 'Fahad Al-Ahmad',
  'الفحيحيل': 'Fahaheel',
  'هدية': 'Hadiya',
  'جابر العلي': 'Jaber Al-Ali',
  'الجنوبية الجواخير': 'Janobyia Aljawakheer',
  'كبد الزراعية': 'kabd Agricultural',
  'الخيران السكنية': 'Khiran City',
  'المقوع': 'Magwa',
  'المهبولة': 'Mahboula',
  'المنقف': 'Mangaf',
  'وسط الأحمدي': 'Middle of Ahmadi',
  'ميناء عبدالله': 'Mina Abdulla',
  'مصفاة ميناء عبدالله': 'Mina Abdullah Refinery',
  'مصفاة ميناء الأحمدي': 'Mina Al-Ahmadi Refinery',
  'شمال الأحمدي': 'North Ahmadi',
  'رجم خشمان': 'Rajim Khashman',
  'الرقة': 'Riqqa',
  'صباح الأحمد 1': 'Sabah Al-Ahmad 1',
  'صباح الأحمد 2': 'Sabah Al-Ahmad 2',
  'صباح الأحمد 3': 'Sabah Al-Ahmad 3',
  'صباح الأحمد 4': 'Sabah Al-Ahmad 4',
  'صباح الأحمد 5': 'Sabah Al-Ahmad 5',
  'صباح الأحمد 6': 'Sabah Al-Ahmad 6',
  'صباح الاحمد البحرية': 'Sabah Al-Ahmad Al-marine',
  'صباح الأحمد استثمارية': 'Sabah Al-Ahmad Investment',
  'صباح الأحمد الخدمية': 'Sabah Al-Ahmad Services',
  'الصباحية': 'Sabahiya',
  'شاليهات الخيران': 'Shalehat Al-Khiran',
  'شاليهات النويصيب': 'Shalehat Al-Nuwaiseeb',
  'شاليهات بنيدر': 'Shalehat Bneder',
  'شاليهات الضباعية': "Shalehat Dba'ayeh",
  'شاليهات الجليعة': "Shalehat Jlea'a",
  'شاليهات ميناء عبدالله': 'Shalehat Mina Abdullah',
  'شاليهات الزور': 'Shalehat Zoor',
  'الشعيبة الصناعية الشرقية': 'Shuaiba Industrial esterly',
  'الشعيبه الصناعيه الغربيه': 'Shuaiba Industrial Western',
  'جنوب الأحمدي': 'South Ahmadi',
  'جنوب صباح الأحمد': 'South Subah AlAhamd',
  'جنوب صباح الأحمد 1': 'South Subah AlAhamd 1',
  'جنوب صباح الأحمد 10': 'South Subah AlAhamd 10',
  'جنوب صباح الأحمد 11': 'South Subah AlAhamd 11',
  'جنوب صباح الأحمد 2': 'South Subah AlAhamd 2',
  'جنوب صباح الأحمد 3': 'South Subah AlAhamd 3',
  'جنوب صباح الأحمد 4': 'South Subah AlAhamd 4',
  'جنوب صباح الأحمد 5': 'South Subah AlAhamd 5',
  'جنوب صباح الأحمد 6': 'South Subah AlAhamd 6',
  'جنوب صباح الأحمد 7': 'South Subah AlAhamd 7',
  'جنوب صباح الأحمد 8': 'South Subah AlAhamd 8',
  'جنوب صباح الأحمد 9': 'South Subah AlAhamd 9',
  'جنوب صباح الأحمد خدمات': 'South Subah AlAhamd Services',
  'جنوب الصباحية': 'South-Sabahiya',
  'أم حجول': 'Umm Hegoul',
  'الوفرة': 'Wafra',
  'مزارع الوفرة': 'Wafra Farms',
  'الوفرة السكنية': 'Wafra Residential',
  'غرب الأحمدي': 'West Ahmadi',
  'الزور': 'Zoor',
  'العبدلي': 'Abdally',
  'المطلاع': 'Al Mutlaa',
  'المطلاع السكنية 1': 'Al Mutlaa Residential 1',
  'المطلاع السكنية 10': 'Al Mutlaa Residential 10',
  'المطلاع السكنية 11': 'Al Mutlaa Residential 11',
  'المطلاع السكنية 12': 'Al Mutlaa Residential 12',
  'المطلاع السكنية 2': 'Al Mutlaa Residential 2',
  'المطلاع السكنية 3': 'Al Mutlaa Residential 3',
  'المطلاع السكنية 4': 'Al Mutlaa Residential 4',
  'المطلاع السكنية 5': 'Al Mutlaa Residential 5',
  'المطلاع السكنية 6': 'Al Mutlaa Residential 6',
  'المطلاع السكنية 7': 'Al Mutlaa Residential 7',
  'المطلاع السكنية 8': 'Al Mutlaa Residential 8',
  'المطلاع السكنية 9': 'Al Mutlaa Residential 9',
  'الشقايا': 'Al Sheqaya',
  'السكراب': 'Al-Sikrab',
  'أمغرة الصناعية': 'Amghara Industrial',
  'بر محافظة الجهراء': 'Bar Al-Jahra Governorate',
  'البحيث': 'Bhaith',
  'جزيرة بوبيان': 'Bubyan Island',
  'الجهراء': 'Jahra',
  'معسكرات الجهراء': 'Jahra Camps',
  'الجهراء الصناعية الحرفية 1': 'Jahra Industrial Herafiya 1',
  'جواخير الجهراء': 'Jawakher Al Jahra',
  'كبد': 'Kabd',
  'القيروان': 'Kaerawan',
  'كاظمة': 'Kazima',
  'منخفضة التكاليف': 'Low costs',
  'النعايم الصناعية': 'Naayem Industrial',
  'النعيم': 'Naeem',
  'النهضة': 'Nahda',
  'النسيم': 'Nasseem',
  'شمال غرب الجهراء': 'North West Jahra',
  'العيون': 'Oyoun',
  'القصر': 'Qasr',
  'الروضتين': 'Rawdatain',
  'سعد العبدالله': 'Saad Al-Abdulla',
  'السالمي': 'Salmy',
  'شاليهات كاظمة': 'Shalehat Kazima',
  'شاليهات الصبية': 'Shalehat Subiya',
  'جنوب أمغره': 'South Amghara',
  'جنوب سعد العبدالله 1': 'South Saad Al-Abdulla 1',
  'جنوب سعد العبدالله 2': 'South Saad Al-Abdulla 2',
  'جنوب سعد العبدالله 3': 'South Saad Al-Abdulla 3',
  'جنوب سعد العبدالله 4': 'South Saad Al-Abdulla 4',
  'جنوب سعد العبدالله 5': 'South Saad Al-Abdulla 5',
  'جنوب سعد العبدالله 6': 'South Saad Al-Abdulla 6',
  'الصبية': 'Subiya',
  'مقبرة الصليبيخات': 'Sulaibikhat Cemetery',
  'الصليبية الزراعية': 'Sulaibiya Agricultural',
  'الصليبية الصناعية 1': 'Sulaibiya Industrial 1',
  'الصليبية الصناعية 2': 'Sulaibiya Industrial 2',
  'الصليبية السكنية': 'Sulaibiya Residential',
  'تيماء': 'Taima',
  'ام العيش': 'Umm Al-Aish',
  'الواحة': 'Waha',
  'جزيرة وربة': 'Warba Island',
  'البدع': "Al Bida'a",
  'الصديق': 'Al-Siddiq',
  'بيان': 'Bayan',
  'حولي': 'Hawalli',
  'حطين': 'Hitteen',
  'الجابرية': 'Jabriya',
  'منطقة الوزارات': 'Ministries Area',
  'مشرف': 'Mishrif',
  'مبارك العبدالله': 'Mubarak Al-Abdullah',
  'المباركية': 'Mubarakyia',
  'الرميثية': 'Rumaithiya',
  'السلام': 'Salam',
  'السالمية': 'Salmiya',
  'سلوى': 'Salwa',
  'الشعب': 'Shaab',
  'الشهداء': 'Shuhada',
  'الواجهة البحرية حولي': 'The Sea Front Hawalli',
  'الزهراء': 'Zahra',
  'ضاحية عبدالله السالم': 'Abdulla Al-Salem',
  'العديلية': 'Adailiya',
  'حدائق السور': 'Al Sour Gardens',
  'جزيرة عوهة': 'Auha Island',
  'بنيد القار': 'Bnaid Al-Qar',
  'الدعية': 'Daiya',
  'الدسمة': 'Dasma',
  'دسمان': 'Dasman',
  'الدوحة السكنية': 'Doha Residential',
  'الفيحاء': 'Faiha',
  'جزيرة فيلكا': 'Failaka Island',
  'غرناطة': 'Ghornata',
  'جابر الأحمد': 'Jaber Al-Ahmad',
  'الخالدية': 'Khaldiya',
  'كيفان': 'Kifan',
  'جزيرة كبر': 'Kubbar Island',
  'المنصورية': 'Mansouriya',
  'ميناء الدوحة': 'Mina Doha',
  'المرقاب': 'Mirqab',
  'جزيرة مسكان': 'Mischan Island',
  'معسكرات المباركية': 'Mubarakiya Camps',
  'جزيرة صناعية شمالية': 'Northern Artificial island',
  'شمال غرب الصليبيخات': 'Northwest Sulaibikhat',
  'النزهة': 'Nuzha',
  'القادسية': 'Qadsiya',
  'جزيرة قاروه': 'Qaruh Island',
  'القبلة': 'Qibla',
  'قرطبة': 'Qortuba',
  'الري': 'Rai',
  'الروضة': 'Rawda',
  'شاليهات الدوحة': 'Shalehat Doha',
  'الشامية': 'Shamiya',
  'الشرق': 'Sharq',
  'الشويخ': 'Shuwaikh',
  'الشويخ التعليمية': 'Shuwaikh Educational',
  'الشويخ الصناعية 1': 'Shuwaikh Industrial-1',
  'الشويخ الصناعية 2': 'Shuwaikh Industrial-2',
  'الشويخ الصناعية 3': 'Shuwaikh Industrial-3',
  'المنطقة الحرة': 'Shuwaikh Port',
  'الشويخ الصحية': 'Shuwaikh Sanitary',
  'جزيرة صناعية جنوبية': 'Southern Artificial Island',
  'الصليبيخات': 'Sulaibikhat',
  'السرة': 'Surra',
  'الواجهة البحرية': 'The Sea Front',
  'جزيرة أم المرادم': 'Umm Al-Maradim Island',
  'جزيرة أم النمل': 'Umm Al-Namel Island',
  'اليرموك': 'Yarmouk',
  'عبدالله مبارك الصباح': 'Abdullah Mubarak Al-Sabah',
  'المطار': 'Airport',
  'الأندلس': 'Andalus',
  'العارضية': 'Ardhiya',
  'العارضية الحرفية': 'Ardhiya Herafiya',
  'العارضية مخازن': 'Ardhiya Stores',
  'العارضية حكومي': 'Ardiya Government',
  'أشبيلية': 'Ashbeliah',
  'الضجيج': 'Dajeej',
  'الفروانية': 'Farwaniya',
  'الفردوس': 'Ferdous',
  'جليب الشيوخ': 'Jleeb Al-Shiyoukh',
  'خيطان': 'Khaitan',
  'العمرية': 'Omariya',
  'الرابية': 'Rabiya',
  'الرحاب': 'Rehab',
  'الرقعي': 'Riggai',
  'صباح السالم الجامعية': 'Sabah Al Salem University',
  'صباح الناصر': 'Sabah Al-Nasser',
  'صيهد العوازم': 'Sayhad Al Awazim',
  'الشدادية': 'Shadadiya',
  'جنوب عبدالله المبارك': 'South Abdullah Al-Mubarak',
  'معارض جنوب خيطان': 'South Khitan Shows',
  'الصليبية الصناعية 3': 'Sulaibyia Industrial 3',
  'غرب عبدالله المبارك': 'West Abdullah Al-Mubarak',
  'ضاحية أبو فطيرة': 'Abu Ftaira',
  'المسايل': 'Al Masayel',
  'العدان': 'Al-Adan',
  'الفنيطيس': 'Al-Fnaitees',
  'القرين': 'Al-Qurain',
  'القصور': 'Al-Qusour',
  'مبارك الكبير': 'Mubarak Al-Kabeer',
  'صباح السالم': 'Sabah Al-Salem',
  'صبحان الصناعية': 'Subhan Industrial',
  'غرب أبو فطيرة الحرفية': 'West Abu Ftirah Hirafyia',
  'المنطقة الوسطى': 'Wista',
};

export function resolveArea(rawArea) {
  if (!rawArea) return null;
  const trimmed = String(rawArea).trim();
  // 1. Exact English match (case-insensitive)
  const en = KUWAIT_AREAS.find(a => a.toLowerCase() === trimmed.toLowerCase());
  if (en) return en;
  // 2. Exact Arabic match
  if (AREA_AR_TO_EN[trimmed]) return AREA_AR_TO_EN[trimmed];
  // 3. Arabic match ignoring leading definite article "ال" differences
  const noAl = trimmed.replace(/^ال/, '');
  const arKey = Object.keys(AREA_AR_TO_EN).find(k => k === noAl || k.replace(/^ال/, '') === noAl || k.replace(/^ال/, '') === trimmed);
  if (arKey) return AREA_AR_TO_EN[arKey];
  // 4. Partial containment (handles extra words in address strings)
  const partial = Object.keys(AREA_AR_TO_EN).find(k => trimmed.includes(k) || k.includes(trimmed));
  if (partial) return AREA_AR_TO_EN[partial];
  // 5. No match — return raw value (better filled than empty; field accepts free text)
  return trimmed;
}

export const CANCELLATION_REASON_OPTIONS = [
  'Delivery too late',
  'Order placed by mistake',
  'Changed mind — no longer needed',
  'Found cheaper elsewhere',
  'Duplicate order',
  'Product not available',
  'Payment issue — unable to complete',
  'Switching to different product size',
  'Moving to different address / area',
  'Business closed / no longer operating',
  'Driver did not arrive',
  'Customer travelling / not home',
  'Other — see description',
];

export const ICARRY_APPLICABLE_GROUPS = [
  'delay_complaint',
  'missing_items',
  'general_inquiry',
];

// Product SKU catalog — grouped by category with an explicit is_private_label
// flag on the 6 Private Label SKUs. Names are the display values stored
// verbatim in tickets.product_sku (no SAP code linkage — names are unique +
// human-readable). Whitespace inside each name is intentional and preserved
// verbatim from the source list (some rows use double-space, some single —
// do not normalize).
export const SKU_OPTIONS = [
  { category: 'Abraaj', items: [
    { sku: 'Abraaj  1.5Ltr x 6',              is_private_label: false, productType: '1.5L' },
    { sku: 'Abraaj CRT  1.5Ltr x 12',         is_private_label: false, productType: '1.5L' },
    { sku: 'Abraaj  200ML x 12',              is_private_label: false, productType: '200ml' },
    { sku: 'Abraaj SN  200ML x 12',           is_private_label: false, productType: '200ml' },
    { sku: 'Abraaj  200ML x 20',              is_private_label: false, productType: '200ml' },
    { sku: 'Abraaj SN  200ML x 20',           is_private_label: false, productType: '200ml' },
    { sku: 'Dabdoob  200ML x 20',             is_private_label: false, productType: '200ml' },
    { sku: 'Dabdoob - SN  200ML x 20',        is_private_label: false, productType: '200ml' },
    { sku: 'Abraaj  330ML x 12',              is_private_label: false, productType: '330ml' },
    { sku: 'Abraaj SN  330ML x 12',           is_private_label: false, productType: '330ml' },
    { sku: 'Abraaj  330ML x 20',              is_private_label: false, productType: '330ml' },
    { sku: 'Abraaj (Alkaline)  330ML x 20',   is_private_label: false, productType: '330ml' },
    { sku: 'Abraaj SN  330ML x 20',           is_private_label: false, productType: '330ml' },
    { sku: 'Abraaj Ice Cube  495ML x 6',      is_private_label: false, productType: 'Ice Cubes' },
    { sku: 'Abraaj  500ML x 12',              is_private_label: false, productType: '500ml' },
    { sku: 'Abraaj CRT  500ML x 20',          is_private_label: false, productType: '500ml' },
    { sku: 'Abraaj SW  500ML x 20',           is_private_label: false, productType: '500ml' },
    { sku: '5 Gallon - NR  5G-NR x 1',        is_private_label: false, productType: '5 Gallon (19L)' },
  ]},
  { category: 'Vista', items: [
    { sku: 'Vista SN  200ML x 12',            is_private_label: false, productType: '200ml' },
    { sku: 'Vista SN  200ML x 20',            is_private_label: false, productType: '200ml' },
    { sku: 'Vista SN  330ML x 12',            is_private_label: false, productType: '330ml' },
  ]},
  { category: 'Drops', items: [
    { sku: 'Epic - Drops  1.5Ltr x 6',        is_private_label: false, productType: '1.5L' },
    { sku: 'Epic - Drops  200ML x 12',        is_private_label: false, productType: '200ml' },
    { sku: 'Epic - Drops SN  200ML x 12',     is_private_label: false, productType: '200ml' },
    { sku: 'Epic - Drops  200ML x 20',        is_private_label: false, productType: '200ml' },
    { sku: 'Epic - Drops  330ML x 12',        is_private_label: false, productType: '330ml' },
    { sku: 'Epic - Drops  330ML x 20',        is_private_label: false, productType: '330ml' },
    { sku: 'Epic - Drops  500ML x 12',        is_private_label: false, productType: '500ml' },
    { sku: 'Epic - Drops (Alkaline)  750ML x 6', is_private_label: false, productType: 'Other' },
  ]},
  { category: 'Tmart', items: [
    { sku: 'Talabat - Tmart  1.5Ltr x 6',     is_private_label: false, productType: '1.5L' },
    { sku: 'Talabat - Tmart  200ML x 12',     is_private_label: false, productType: '200ml' },
    { sku: 'Talabat - Tmart SN  200ML x 20',  is_private_label: false, productType: '200ml' },
    { sku: 'Talabat Alkaline Water  330ML x 20', is_private_label: false, productType: '330ml' },
    { sku: 'Talabat-Mart SN  330ML x 20',     is_private_label: false, productType: '330ml' },
    { sku: 'Talabat - Tmart  330ML x 20',     is_private_label: false, productType: '330ml' },
    { sku: 'Talabat - Tmart  500ML x 12',     is_private_label: false, productType: '500ml' },
  ]},
  { category: 'Import (Glass & PET)', items: [
    { sku: 'Abraaj Sparkling Water Glass  250ML x 6', is_private_label: false, productType: 'Other' },
    { sku: 'Abraaj Still Water Glass 250ML x 6',      is_private_label: false, productType: 'Other' },
    { sku: 'Abraaj Sparkling Water Glass  750ML x 6', is_private_label: false, productType: 'Other' },
    { sku: 'Abraaj Sport Cap Water PET 750ML x 6',    is_private_label: false, productType: 'Other' },
    { sku: 'Abraaj Still Water Glass 750ML x 6',      is_private_label: false, productType: 'Other' },
  ]},
  { category: 'Private Labels', items: [
    { sku: 'Private label 1.5Ltr x 6',        is_private_label: true,  productType: '1.5L' },
    { sku: 'Private label  200ML x 20',       is_private_label: true,  productType: '200ml' },
    { sku: 'Private label 330ML x 20',        is_private_label: true,  productType: '330ml' },
    { sku: 'Private label  500ML x 12',       is_private_label: true,  productType: '500ml' },
    { sku: 'Private label SW  500ML x 20',    is_private_label: true,  productType: '500ml' },
    { sku: 'Private label 5G-NR x 1',         is_private_label: true,  productType: '5 Gallon (19L)' },
  ]},
];

// O(1) lookup: SKU display name → true if that SKU is a Private Label.
// Callsite: SKU_IS_PRIVATE_LABEL[form.product_sku] gates the brand-name input.
export const SKU_IS_PRIVATE_LABEL = Object.fromEntries(
  SKU_OPTIONS.flatMap((cat) => cat.items.map((it) => [it.sku, !!it.is_private_label]))
);

// O(1) lookup: SKU display name → its Product Type value (matches PRODUCT_TYPE_OPTIONS).
// Callsite: picking a SKU on NewTicket auto-fills product_type from this map
// (overwrite / last-pick-wins). Values are the exact PRODUCT_TYPE_OPTIONS
// strings so the auto-filled state matches the checkbox source of truth.
export const PRODUCT_TYPE_BY_SKU = Object.fromEntries(
  SKU_OPTIONS.flatMap((cat) => cat.items.map((it) => [it.sku, it.productType]))
);

// Complaint groups where the SKU/quantity block is shown (never required).
// Mirrors the ICARRY_APPLICABLE_GROUPS scoping pattern.
export const SKU_APPLICABLE_GROUPS = ['quality_complaint', 'missing_items'];

// Sample stage vocabulary — mirrors backend VALID_SAMPLE_STAGES + STAGE_OWNERS.
// Ordered pointer: null → 'logistics' → 'warehouse' → 'quality-control'.
// Slugs HYPHENATED to match escalation slug vocabulary — no transform needed
// for the dept gate; slugsForDepartment output is directly comparable.
//
// ownerDeptSlugs is an ARRAY on every stage (uniform shape) — multi-owner
// stages list every allowed slug. Warehouse allows Logistics OR QC because
// no Warehouse users exist yet; when onboarded, tighten to ['warehouse']. Any
// change here MUST be mirrored in backend/src/routes/tickets.js STAGE_OWNERS.
// super_admin/entity_manager are always allowed and NOT listed in the array.
export const SAMPLE_STAGES = [
  { slug: 'logistics',       label: 'With Logistics',      shortLabel: 'Logistics', ownerDeptSlugs: ['logistics'],                    timestampField: 'sample_logistics_at' },
  { slug: 'warehouse',       label: 'At Warehouse',        shortLabel: 'Warehouse', ownerDeptSlugs: ['logistics', 'quality-control'], timestampField: 'sample_warehouse_at' },
  { slug: 'quality-control', label: 'Received at QC Lab',  shortLabel: 'QC Lab',    ownerDeptSlugs: ['quality-control'],              timestampField: 'sample_quality_at' },
];
export const SAMPLE_STAGE_BY_SLUG = Object.fromEntries(SAMPLE_STAGES.map((s) => [s.slug, s]));
export const SAMPLE_APPLICABLE_GROUPS = ['quality_complaint'];

// Quantity unit — DB stores canonical lowercase; UI shows title-case labels.
export const QUANTITY_UNIT_OPTIONS = [
  { value: 'pack',   label: 'Pack'   },
  { value: 'bottle', label: 'Bottle' },
];
export const QUANTITY_UNIT_LABEL = Object.fromEntries(
  QUANTITY_UNIT_OPTIONS.map((o) => [o.value, o.label])
);

// Source of truth for display labels. DB always stores the slug.
export const COMPLAINT_GROUPS = [
  { slug: 'delay_complaint',    label: 'Delivery' },
  { slug: 'missing_items',      label: 'Missing Items' },
  { slug: 'quality_complaint',  label: 'Product Quality' },
  { slug: 'cancel_order',       label: 'Cancel Order' },
  { slug: 'refund_request',     label: 'Payment & Refund' },
  { slug: 'driver_complaint',   label: 'Driver & Staff' },
  { slug: 'general_inquiry',    label: 'General Inquiry' },
  { slug: 'business_inquiry',   label: 'Business Inquiry' },
  { slug: 'price_inquiry',      label: 'Price Inquiry' },
  { slug: 'new_order',          label: 'New Order' },
  { slug: 'sales_call_request', label: 'Sales Call Request' },
  { slug: 'mosque_inquiry',     label: 'Mosque Inquiry' },
  { slug: 'subscription',       label: 'Subscription' },
  { slug: 'other',              label: 'Other' },
  { slug: 'order_modification', label: 'Order Modification' },
  { slug: 'payment_link',       label: 'Payment Link' },
  { slug: 'delivery_inquiry',   label: 'Delivery Inquiry' },
];

export const COMPLAINT_GROUP_LABEL = Object.fromEntries(
  COMPLAINT_GROUPS.map(g => [g.slug, g.label])
);

// Per-entity routable departments — single source of truth for the routing
// rules modal, ticket-detail escalate dropdown, and dept notification emails.
// Mirrored in backend/src/constants/labels.js — keep the two in sync.
export const DEPT_OPTIONS_BY_ENTITY = {
  'abraaj-water': [
    { slug: 'delivery-supervisor', label: 'Delivery Supervisor' },
    { slug: 'quality-control',     label: 'Quality Control' },
    { slug: 'finance',             label: 'Finance' },
    { slug: 'logistics-manager',   label: 'Logistics Manager' },
    { slug: 'marketing',           label: 'Marketing' },
  ],
  'lofat-lifestyle': [
    { slug: 'kitchen',             label: 'Kitchen' },
    { slug: 'logistics',           label: 'Logistics' },
    { slug: 'finance',             label: 'Finance' },
    { slug: 'quality-control',     label: 'Quality Control' },
    { slug: 'dietitians',          label: 'Dietitians' },
  ],
  'regency-kuwait': [
    { slug: 'housekeeping',        label: 'Housekeeping' },
    { slug: 'engineering',         label: 'Engineering' },
    { slug: 'fnb',                 label: 'F&B' },
    { slug: 'front-office',        label: 'Front Office' },
  ],
};

// Flat, deduped list of all departments across entities.
export const ALL_DEPT_OPTIONS = Object.values(DEPT_OPTIONS_BY_ENTITY).flat()
  .filter((d, i, arr) => arr.findIndex(x => x.slug === d.slug) === i);

// Department-slug families — mirror of backend/src/constants/labels.js
// SUBROLE_FAMILIES. A user with a given department can act on tickets whose
// escalated_to_dept matches any slug in their family. Only non-trivial
// families declared; everything else is 1:1 by construction.
//
// MUST stay in sync with backend/src/constants/labels.js SUBROLE_FAMILIES.
// Any change here MUST be mirrored there (and vice-versa).
export const SUBROLE_FAMILIES = {
  'logistics': ['delivery-supervisor', 'logistics-manager'],
};

export function labelToSlug(label) {
  return String(label || '').toLowerCase().replace(/\s+/g, '-');
}

export function slugsForDepartment(departmentLabel) {
  const own = labelToSlug(departmentLabel);
  if (!own) return [];
  const subroles = SUBROLE_FAMILIES[own] || [];
  return [own, ...subroles];
}

export function departmentMatchesEscalation(departmentLabel, escalatedToDept) {
  if (!escalatedToDept) return false;
  return slugsForDepartment(departmentLabel).includes(String(escalatedToDept).toLowerCase());
}
